import { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';

import {
  assessmentsApi,
  type AttemptPaper,
  type AttemptResult,
  type AttemptSummary,
} from '@/lib/api/assessments';

/** How long after the last keystroke/selection we push answers to the server. */
export const AUTOSAVE_DEBOUNCE_MS = 800;

type ApiErrorish = { response?: { status?: number; data?: { error?: string } } };

export function errorStatus(e: unknown): number | undefined {
  return (e as ApiErrorish)?.response?.status;
}

export function errorMessage(e: unknown, fallback: string): string {
  return (e as ApiErrorish)?.response?.data?.error ?? fallback;
}

export interface UseAttemptState {
  loading: boolean;
  /** Fatal load error (403 window closed, 409 attempts exhausted, 422 empty…). */
  startError: string | null;
  paper: AttemptPaper | null;
  /** The client clock read at the instant `paper` arrived, for skew measurement. */
  clientNowAtStart: Date | null;
  answers: Record<string, number>;
  result: AttemptResult | null;
  history: AttemptSummary[];
  submitting: boolean;
  saving: boolean;
  /** True once the server has refused writes because the clock ran out. */
  timedOut: boolean;
  /**
   * True when mount found an `in_progress` attempt whose `expires_at` has
   * already passed. Expiry is closed lazily server-side, so that row still
   * reads `in_progress` — we deliberately do NOT auto-resume it (that would
   * silently grade and close it, then start a fresh timed attempt with no
   * gesture from the student). The student sees the pre-start screen instead
   * and must explicitly choose to start again.
   */
  staleExpiredAttempt: boolean;
  /**
   * True when a submit came back 409 past the grace window: the server has
   * already graded and closed the attempt from whatever was autosaved, but
   * this call carries no AttemptResult payload. Lets the UI show a resolved
   * state (history) instead of a frozen, scoreless paper.
   */
  closedWithoutResult: boolean;
  select: (questionID: string, displayIndex: number) => void;
  /** Returns true once settled (submitted, or definitively rejected); false
   *  means a transient failure the caller may retry. */
  submit: (reason: 'manual' | 'expiry') => Promise<boolean>;
  start: () => Promise<void>;
  refreshHistory: () => Promise<void>;
}

/**
 * useAttempt owns one student's timed attempt: it starts (or resumes) the
 * attempt, keeps the answer map, autosaves it with a debounce and submits.
 *
 * Answers are keyed by question id and valued by the index into the PERMUTED
 * options the student was shown — never by array position, which is meaningless
 * once the paper is shuffled and sampled per attempt.
 */
export function useAttempt(assessmentID: string, enabled: boolean): UseAttemptState {
  const [loading, setLoading] = useState(true);
  const [startError, setStartError] = useState<string | null>(null);
  const [paper, setPaper] = useState<AttemptPaper | null>(null);
  const [clientNowAtStart, setClientNowAtStart] = useState<Date | null>(null);
  const [answers, setAnswers] = useState<Record<string, number>>({});
  const [result, setResult] = useState<AttemptResult | null>(null);
  const [history, setHistory] = useState<AttemptSummary[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [timedOut, setTimedOut] = useState(false);
  const [staleExpiredAttempt, setStaleExpiredAttempt] = useState(false);
  const [closedWithoutResult, setClosedWithoutResult] = useState(false);

  // Refs the timers read, so the debounce and the expiry handler always see the
  // latest values without being torn down and rebuilt on every keystroke.
  const answersRef = useRef<Record<string, number>>({});
  const attemptIDRef = useRef<string | null>(null);
  const timedOutRef = useRef(false);
  const submittedRef = useRef(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const refreshHistory = useCallback(async () => {
    try {
      setHistory(await assessmentsApi.listMyAttempts(assessmentID));
    } catch {
      /* history is decorative; a failure must not break the paper */
    }
  }, [assessmentID]);

  const start = useCallback(async () => {
    setLoading(true);
    setStartError(null);
    try {
      const p = await assessmentsApi.startAttempt(assessmentID);
      // A paper with no questions is not a paper. The server now refuses to
      // serve one (422), but this stays as the client-side floor: accepting a
      // zero-question paper is what produced the blank quiz — a running clock,
      // "Attempt 1 of 3" and autosave rendered over "Question 1 / 0", with
      // nothing thrown and no console error to explain it. Surface it as a
      // start error on the pre-start screen instead of a live attempt.
      if (!p.questions || p.questions.length === 0) {
        setStartError('This quiz has no questions available right now. Please contact your trainer.');
        return;
      }
      // Read the client clock as close to the response as possible: this pairs
      // with p.server_now to measure skew for the countdown.
      setClientNowAtStart(new Date());
      setPaper(p);
      attemptIDRef.current = p.attempt_id;
      answersRef.current = p.answers ?? {};
      setAnswers(p.answers ?? {});
      submittedRef.current = false;
      timedOutRef.current = false;
      setTimedOut(false);
      setResult(null);
      setStaleExpiredAttempt(false);
      setClosedWithoutResult(false);
      if (p.resumed) toast.info('Resumed your attempt in progress.');
    } catch (e) {
      setStartError(errorMessage(e, 'Could not start this quiz.'));
    } finally {
      setLoading(false);
    }
  }, [assessmentID]);

  const startRef = useRef(start);
  startRef.current = start;

  useEffect(() => {
    if (!enabled) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    (async () => {
      let hist: AttemptSummary[] = [];
      try {
        hist = await assessmentsApi.listMyAttempts(assessmentID);
      } catch {
        /* history is decorative; a failure must not break the paper */
      }
      if (cancelled) return;
      setHistory(hist);
      // A reload mid-quiz must land straight back in the paper rather than on
      // a "Start" screen the student has to click through while the clock runs.
      // StartAttempt resumes an open attempt instead of burning a new one.
      //
      // BUT: expiry is closed lazily server-side, so an attempt whose deadline
      // has already passed still reads `in_progress` here. Auto-calling start()
      // for THAT attempt would silently grade-and-close it, then immediately
      // create a brand-new timed attempt with no gesture from the student — a
      // reload-to-check-your-score could burn a retake with no confirmation and
      // no "resumed" toast (resumed is false for the newly created attempt).
      // Only auto-resume attempts that are genuinely still open; anything else
      // falls through to the pre-start screen so starting again is deliberate.
      const openAttempt = hist.find((a) => a.status === 'in_progress');
      if (openAttempt) {
        const expiresAt = openAttempt.expires_at ? new Date(openAttempt.expires_at) : null;
        const stillOpen = !expiresAt || expiresAt.getTime() > Date.now();
        if (stillOpen) {
          await startRef.current();
          return;
        }
        setStaleExpiredAttempt(true);
      }
      if (!cancelled) setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [enabled, assessmentID]);

  const flush = useCallback(async () => {
    const attemptID = attemptIDRef.current;
    if (!attemptID || timedOutRef.current || submittedRef.current) return;
    setSaving(true);
    try {
      await assessmentsApi.saveAttemptAnswers(attemptID, answersRef.current);
    } catch (e) {
      if (errorStatus(e) === 409) {
        // The server (or the database clock) says this attempt no longer takes
        // answers. Surface it as "time is up", not as a raw failure, and stop
        // hammering the endpoint.
        timedOutRef.current = true;
        setTimedOut(true);
        toast.warning('Time is up — your last saved answers will be graded.');
      } else {
        toast.error('Could not save your answer. Check your connection.');
      }
    } finally {
      setSaving(false);
    }
  }, []);

  const select = useCallback(
    (questionID: string, displayIndex: number) => {
      if (timedOutRef.current || submittedRef.current) return;
      answersRef.current = { ...answersRef.current, [questionID]: displayIndex };
      setAnswers(answersRef.current);
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => void flush(), AUTOSAVE_DEBOUNCE_MS);
    },
    [flush],
  );

  const submit = useCallback(
    async (reason: 'manual' | 'expiry'): Promise<boolean> => {
      const attemptID = attemptIDRef.current;
      // Nothing to do (already submitted) counts as settled, not a failure to retry.
      if (!attemptID || submittedRef.current) return true;
      submittedRef.current = true;
      if (debounceRef.current) clearTimeout(debounceRef.current);
      setSubmitting(true);
      try {
        // Submitting the full answer map means a pending debounce that never
        // fired cannot lose the student's last selection. The server accepts a
        // submit for 30s past expiry, which is what makes the expiry path work.
        const res = await assessmentsApi.submitAttempt(attemptID, answersRef.current);
        setResult(res);
        toast.success(reason === 'expiry' ? 'Time is up — your quiz was submitted.' : 'Quiz submitted.');
        void refreshHistory();
        return true;
      } catch (e) {
        submittedRef.current = false;
        if (errorStatus(e) === 409) {
          // Past the grace window, or already finished: the server has graded
          // and closed the attempt from whatever was autosaved, but this
          // response carries no AttemptResult payload. Surface a resolved
          // state (the history, with the score) rather than leaving the
          // student stranded on a frozen paper with only a banner.
          toast.warning(errorMessage(e, 'This attempt is already finished.'));
          setTimedOut(true);
          timedOutRef.current = true;
          setClosedWithoutResult(true);
          void refreshHistory();
          return true; // settled — a definitive rejection, not worth retrying
        }
        toast.error(errorMessage(e, 'Could not submit your quiz.'));
        return false; // transient failure — caller (auto-submit) may retry
      } finally {
        setSubmitting(false);
      }
    },
    [refreshHistory],
  );

  useEffect(
    () => () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    },
    [],
  );

  return {
    loading,
    startError,
    paper,
    clientNowAtStart,
    answers,
    result,
    history,
    submitting,
    saving,
    timedOut,
    staleExpiredAttempt,
    closedWithoutResult,
    select,
    submit,
    start,
    refreshHistory,
  };
}
